/**
 * The check batch's pure rules (piece 24, Checks to Print; decisions 0556, 0558).
 *
 * 🔨 The stock is pre-printed and pre-numbered, so this application draws no
 * check number. A person prints a stack, reads the paper, and types the number
 * the first sheet carried; every other number in the batch follows from it by
 * one, up or down the list depending on how the printer took the stock.
 */
import { t } from '../strings';

/** Which way the numbers run down the printed stack; a printer that needs the pages flipped runs backwards. */
export const CHECK_NUMBERING_DIRECTIONS = ['Ascending', 'Descending'] as const;
export type CheckNumberingDirection = (typeof CHECK_NUMBERING_DIRECTIONS)[number];

/** The Settings key piece 24 declares for the direction, seeded Ascending. */
export const CHECK_NUMBERING_DIRECTION_KEY = 'checks.numberingDirection';
export const CHECK_NUMBERING_DIRECTION_DEFAULT: CheckNumberingDirection = 'Ascending';

export function isCheckNumberingDirection(value: unknown): value is CheckNumberingDirection {
  return typeof value === 'string' && (CHECK_NUMBERING_DIRECTIONS as readonly string[]).includes(value);
}

/** The one sentence a bad direction carries, so the Settings screen and the dialog refuse it the same way. */
export function checkNumberingDirectionProblem(value: unknown): string | null {
  return isCheckNumberingDirection(value) ? null : t('checks.direction.problem');
}

/**
 * 🔨 A check number is digits on paper. A blank, a letter or a space is refused
 * before anything is written, because a batch numbered from a typo is a batch of
 * wrong numbers in the ledger and the paper cannot be un-printed.
 */
export function checkStartNumberProblem(start: string | null | undefined): string | null {
  const text = (start ?? '').trim();
  if (text === '') return t('checks.start.required');
  if (!/^[0-9]+$/.test(text)) return t('checks.start.digits');
  if (Number(text) === 0) return t('checks.start.digits');
  return null;
}

/** The width a typed number keeps: 0042 stays four wide, 42 grows as it needs to. */
function widthOf(start: string): number {
  return start.startsWith('0') ? start.length : 0;
}

function render(value: number, width: number): string {
  return width > 0 ? String(value).padStart(width, '0') : String(value);
}

/**
 * The numbers one batch consumes, in list order. Ascending runs up the list from
 * the start; Descending runs down it, which is what a printer that takes the
 * stock upside down produces.
 *
 * 🔨 The page order of the sheet never changes — only which number each row is
 * about — so the PDF a person holds and the dialog they read are the same order.
 */
export function checkNumberSequence(start: string, count: number, direction: CheckNumberingDirection = CHECK_NUMBERING_DIRECTION_DEFAULT): string[] {
  if (checkStartNumberProblem(start) !== null || count <= 0) return [];
  const first = Number(start.trim());
  const width = widthOf(start.trim());
  const step = direction === 'Descending' ? -1 : 1;
  const out: string[] = [];
  for (let index = 0; index < count; index += 1) {
    const value = first + step * index;
    if (value < 1) return out;
    out.push(render(value, width));
  }
  return out;
}

/** 🔨 A descending batch cannot run off the bottom of the stock: the numbers it needs have to exist. */
export function checkSequenceProblem(start: string, count: number, direction: CheckNumberingDirection = CHECK_NUMBERING_DIRECTION_DEFAULT): string | null {
  const problem = checkStartNumberProblem(start);
  if (problem) return problem;
  if (count <= 0) return t('checks.batch.empty');
  return checkNumberSequence(start, count, direction).length === count ? null : t('checks.start.tooLow');
}

/**
 * The last number the stock gave up, whether or not every sheet printed
 * correctly. 🔨 An unticked row's number is consumed all the same: the paper went
 * through the printer and it is not going back in the tray.
 */
export function lastCheckNumberOf(start: string, count: number, direction: CheckNumberingDirection = CHECK_NUMBERING_DIRECTION_DEFAULT): string | null {
  const sequence = checkNumberSequence(start, count, direction);
  return sequence.length === 0 ? null : sequence[sequence.length - 1]!;
}

/**
 * The number the next batch on this account is proposed at: one past the last
 * number the account's stock gave up, in the direction in force. A person can
 * overtype it, because the tray is the authority and this is a memory.
 */
export function nextCheckStartNumber(lastNumber: string | null | undefined, direction: CheckNumberingDirection = CHECK_NUMBERING_DIRECTION_DEFAULT): string | null {
  const text = (lastNumber ?? '').trim();
  if (checkStartNumberProblem(text) !== null) return null;
  const value = Number(text) + (direction === 'Descending' ? -1 : 1);
  return value < 1 ? null : render(value, widthOf(text));
}

/**
 * 🔒 A batch is one bank account's stock (decision 0556). The numbers run from
 * one sheet of paper through one tray, so a batch spanning two accounts would
 * number both accounts' checks from one sequence and leave each account
 * remembering the other's last number.
 *
 * 🔨 It is refused before anything is written, and the refusal names the
 * accounts rather than saying only that they differ.
 */
export function checkBatchAccountProblem(accountNames: readonly string[]): string | null {
  const distinct = [...new Set(accountNames.map((name) => name.trim()).filter((name) => name !== ''))];
  if (distinct.length <= 1) return null;
  return `${t('checks.batch.oneAccount')} ${distinct.sort().join(', ')}`;
}

/**
 * 🔨 A batch names each check once (decision 0556). A repeated identifier would
 * consume a number that no sheet carried and leave the account's memory one past
 * where the paper stopped.
 */
export function checkBatchIdsProblem(ids: readonly string[]): string | null {
  if (ids.length === 0) return t('checks.batch.empty');
  return new Set(ids).size === ids.length ? null : t('checks.batch.duplicate');
}

/** 🔨 A voided check is not confirmed, and a Printed one is confirmed already; neither takes a second number. */
export function checkConfirmProblem(status: string, checkNumber: string | null): string | null {
  if (status === 'Voided') return t('checks.confirm.voided');
  if (status === 'Printed' || checkNumber) return t('checks.confirm.already');
  return null;
}

/**
 * 🔨 A sheet is served for a check that is waiting or printed, and never for a
 * voided one. Reprinting the sheet of a Printed check is not printing the check
 * twice: the number and the state do not move (decision 0558).
 */
export function checkSheetProblem(status: string): string | null {
  return status === 'Voided' ? t('checks.sheet.voided') : null;
}

/**
 * The memo the check carries (decision 0558): the reference a person typed on
 * the payment run, else the numbers of the bills it settled. 🔨 It is computed
 * at print and stored nowhere, because everything it says is already a fact of
 * the payment.
 */
export function checkMemoText(reference: string | null | undefined, settledNumbers: readonly string[]): string {
  const typed = (reference ?? '').trim();
  if (typed !== '') return typed;
  return settledNumbers.filter((number) => number.trim() !== '').join(', ');
}
